// The interactive reading surface.
//
// Single-text-node contract: the reading body is rendered as one contiguous
// text node inside <section data-reading-body>. Token state (unknown /
// developing / focus) is layered on top via the CSS Custom Highlight API so
// decoration never requires splitting the text into DOM elements.
//
// Coordinate systems:
//   1. Browser DOM (authoritative) — Range.getBoundingClientRect() for popover
//      anchors, document.caretPositionFromPoint() / caretRangeFromPoint() for
//      click hit-testing. These are always correct regardless of how the
//      browser chose to wrap.
//   2. @chenglou/pretext (parallel) — we prepare the body once with the live
//      computed font and run layoutWithLines on every resize. The lineCount
//      is surfaced on the root as data-pretext-lines for wrap-parity
//      verification and as the foundation for future server-side layout work.
//
// Engagement: onTokenEngage is a typed no-op in v0. Phase B will replace the
// single call site with the flow from
// ~/code/sara/app.read-by-ear.com/src/lib/hooks/use-learning-records.tsx:203-253
// without touching this component's DOM or the TokenHit contract.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { prepare, layout, type PreparedText } from "@chenglou/pretext";

import type { ReadingViewData, TokenHit } from "@bindery/ja-core";

import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@bindery/ui/components/ui/popover";

import { TokenCard } from "./token-card";

// The ::highlight() rules for the three highlight names registered below.
// Its own file, and imported HERE rather than from ../../index.css, because
// Lightning CSS errors on that pseudo-element — the file's own header says so.
//
// NOT AN AS-IS PORT: yominoki never imported it from anywhere. The stylesheet
// existed, the package exported it, globals.css's comment told the reader to
// import it "from the reader route", and no route ever did — so the token
// decoration this component registers rendered as nothing. Wiring it up is
// what the comment always intended.
import "../../reader-highlights.css";

interface Props {
  data: ReadingViewData;
}

interface VirtualAnchor {
  getBoundingClientRect: () => DOMRect;
}

// Feature-detect the CSS Custom Highlight API without TypeScript lib help —
// it's not yet in the default lib.dom typings on all versions.
type HighlightConstructor = new (...ranges: Range[]) => HighlightLike;
interface HighlightLike {
  add(range: Range): void;
  clear(): void;
}
interface HighlightRegistry {
  set(name: string, highlight: HighlightLike): void;
  delete(name: string): void;
}

function getHighlightAPI(): {
  Highlight: HighlightConstructor;
  registry: HighlightRegistry;
} | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    Highlight?: HighlightConstructor;
    CSS?: { highlights?: HighlightRegistry };
  };
  if (!w.Highlight || !w.CSS?.highlights) return null;
  return { Highlight: w.Highlight, registry: w.CSS.highlights };
}

function rangeFor(textNode: Text, start: number, end: number): Range {
  const r = document.createRange();
  r.setStart(textNode, Math.min(start, textNode.length));
  r.setEnd(textNode, Math.min(end, textNode.length));
  return r;
}

function hitTestToken(
  tokens: readonly TokenHit[],
  textNode: Text,
  clientX: number,
  clientY: number,
): TokenHit | null {
  // Prefer caretPositionFromPoint (standard), fall back to caretRangeFromPoint
  // (WebKit legacy). Both return an offset into the text node.
  const doc = document as unknown as {
    caretPositionFromPoint?: (
      x: number,
      y: number,
    ) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  let offset: number | null = null;
  if (doc.caretPositionFromPoint) {
    const pos = doc.caretPositionFromPoint(clientX, clientY);
    if (pos && pos.offsetNode === textNode) offset = pos.offset;
  }
  if (offset === null && doc.caretRangeFromPoint) {
    const r = doc.caretRangeFromPoint(clientX, clientY);
    if (r && r.startContainer === textNode) offset = r.startOffset;
  }
  if (offset === null) return null;
  // Tokens are sorted by startOffset; a linear scan is fine for reading-length
  // corpora (≤ a few hundred tokens).
  for (const t of tokens) {
    if (offset >= t.startOffset && offset < t.endOffset) {
      if (t.state === "always-fluent") return null;
      return t;
    }
  }
  return null;
}

export function ReadingBody({ data }: Props) {
  const { body, tokens } = data;

  const bodyRef = useRef<HTMLElement | null>(null);
  const preparedRef = useRef<PreparedText | null>(null);
  const virtualAnchorRef = useRef<VirtualAnchor | null>(null);

  const [openToken, setOpenToken] = useState<TokenHit | null>(null);
  const [focusIdx, setFocusIdx] = useState<number>(-1);

  // Engageable tokens: the subset the keyboard virtual cursor walks.
  const engageable = useMemo(
    () => tokens.filter((t) => t.state !== "always-fluent"),
    [tokens],
  );

  // Phase B seam. Replace the body of this callback with the engagement flow
  // from sara's use-learning-records.tsx (updateFluencyRecords).
  const onTokenEngage = useCallback((token: TokenHit) => {
    // no-op in v0
    void token;
  }, []);

  // Register the CSS Custom Highlights for unknown + developing tokens. This
  // is the primary visual treatment — no DOM splitting required.
  useEffect(() => {
    const section = bodyRef.current;
    if (!section) return;
    const textNode = section.firstChild;
    if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return;
    const api = getHighlightAPI();
    if (!api) {
      // Older browser — degrade gracefully. Tokens still hit-test, popover
      // still works, just no underline decoration. SVG fallback overlay is
      // tracked as Phase B polish in the plan.
      return;
    }
    const unknown = new api.Highlight();
    const developing = new api.Highlight();
    for (const t of tokens) {
      if (t.state === "unknown") {
        unknown.add(rangeFor(textNode as Text, t.startOffset, t.endOffset));
      } else if (t.state === "developing") {
        developing.add(rangeFor(textNode as Text, t.startOffset, t.endOffset));
      }
    }
    api.registry.set("token-unknown", unknown);
    api.registry.set("token-developing", developing);
    return () => {
      api.registry.delete("token-unknown");
      api.registry.delete("token-developing");
    };
  }, [tokens]);

  // Render the virtual-cursor focus as a third Highlight layer.
  useEffect(() => {
    const section = bodyRef.current;
    if (!section) return;
    const textNode = section.firstChild;
    if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return;
    const api = getHighlightAPI();
    if (!api) return;
    const focus = new api.Highlight();
    const t = engageable[focusIdx];
    if (t) {
      focus.add(rangeFor(textNode as Text, t.startOffset, t.endOffset));
    }
    api.registry.set("token-focus", focus);
    return () => {
      api.registry.delete("token-focus");
    };
  }, [engageable, focusIdx]);

  // Pretext parity: prepare once per body, relayout on resize. Exposes
  // lineCount as a data attribute for dev-overlay diffing against the
  // browser's actual wrap.
  useEffect(() => {
    const section = bodyRef.current;
    if (!section) return;
    const style = getComputedStyle(section);
    // pretext README: use a named font family; `system-ui` is unsafe on macOS.
    // We feed it the computed family directly. If the user's browser has
    // substituted a fallback, pretext will diverge — the dev overlay flags it.
    const fontString = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    try {
      preparedRef.current = prepare(body, fontString, {
        whiteSpace: "pre-wrap",
        wordBreak: "keep-all",
      });
    } catch {
      preparedRef.current = null;
      return;
    }

    const applyLayout = () => {
      const width = section.clientWidth;
      const lh = parseFloat(style.lineHeight || "0") || 0;
      if (!preparedRef.current || width <= 0 || lh <= 0) return;
      const { lineCount } = layout(preparedRef.current, width, lh);
      section.setAttribute("data-pretext-lines", String(lineCount));
    };

    applyLayout();
    const ro = new ResizeObserver(applyLayout);
    ro.observe(section);
    return () => ro.disconnect();
  }, [body]);

  const openTokenAt = useCallback(
    (token: TokenHit, rect: DOMRect) => {
      virtualAnchorRef.current = {
        getBoundingClientRect: () => rect,
      };
      setOpenToken(token);
      setFocusIdx(engageable.findIndex((e) => e.flatIdx === token.flatIdx));
      onTokenEngage(token);
    },
    [engageable, onTokenEngage],
  );

  const onSectionClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const section = bodyRef.current;
      if (!section) return;
      const textNode = section.firstChild;
      if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return;
      const token = hitTestToken(
        tokens,
        textNode as Text,
        e.clientX,
        e.clientY,
      );
      if (!token) return;
      const r = rangeFor(textNode as Text, token.startOffset, token.endOffset);
      openTokenAt(token, r.getBoundingClientRect());
    },
    [tokens, openTokenAt],
  );

  const openByFocusIdx = useCallback(
    (idx: number) => {
      const section = bodyRef.current;
      if (!section) return;
      const textNode = section.firstChild;
      if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return;
      const token = engageable[idx];
      if (!token) return;
      const r = rangeFor(textNode as Text, token.startOffset, token.endOffset);
      openTokenAt(token, r.getBoundingClientRect());
    },
    [engageable, openTokenAt],
  );

  const onHostKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (engageable.length === 0) return;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        setFocusIdx((i) => Math.min(engageable.length - 1, i < 0 ? 0 : i + 1));
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        setFocusIdx((i) => Math.max(0, i < 0 ? 0 : i - 1));
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        const idx = focusIdx < 0 ? 0 : focusIdx;
        openByFocusIdx(idx);
      } else if (e.key === "Escape") {
        setOpenToken(null);
      }
    },
    [engageable.length, focusIdx, openByFocusIdx],
  );

  return (
    <>
      {/* oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- a roving keyboard host: arrow keys move between marked tokens and Enter opens one */}
      <div
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- the keyboard host above, not a group of form controls
        role="group"
        aria-roledescription="reading"
        aria-label="Japanese reading. Use arrow keys to move between marked tokens, Enter to open."
        // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- the host's single tab stop; see onHostKeyDown
        tabIndex={0}
        onKeyDown={onHostKeyDown}
        className="focus-visible:outline-persimmon focus:outline-none focus-visible:outline-1 focus-visible:outline-offset-4"
      >
        {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- the pointer path to the tokens the host above reaches from the keyboard */}
        <section
          ref={bodyRef}
          data-reading-body
          itemProp="articleBody"
          onClick={onSectionClick}
          className="font-jp-body text-ink selection:bg-persimmon/20 text-[1.125rem] leading-loose break-keep whitespace-pre-wrap"
          style={{ wordBreak: "keep-all" }}
        >
          {body}
        </section>
      </div>

      <Popover
        open={openToken !== null}
        onOpenChange={(open) => {
          if (!open) setOpenToken(null);
        }}
      >
        <PopoverAnchor virtualRef={virtualAnchorRef} />
        {openToken && (
          <PopoverContent
            side="top"
            align="center"
            className="border-rule bg-paper w-auto max-w-[18rem] border"
          >
            <TokenCard token={openToken} />
          </PopoverContent>
        )}
      </Popover>
    </>
  );
}
